

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLoader } from "../../../../context/LoaderProvider.jsx";
import {
    TrendingUp,
    TrendingDown,
    Eye,
    MousePointer,
    Heart,
    Share2,
    XCircle,
    Users,
    Clock,
    BarChart3,
    Target,
    Activity,
    Award,
    Calendar,
    MapPin,
    Smartphone,
    Globe,
    Download,
    RefreshCw,
    ChevronDown,
    PieChart,
    Zap,
    ThumbsUp,
    AlertCircle,
    Search,
    Filter,
    ChevronLeft,
    ChevronRight,
    Rocket,
    Sparkles,
    TrendingUp as TrendingUpIcon,
    BarChart as BarChartIcon,
    Layers,
    Briefcase,
    CheckCircle,
    AlertTriangle,
    LineChart,
    Percent,
    EyeOff,
    MousePointerClick,
    ThumbsDown,
    TrendingUp as TrendUp,
    TrendingDown as TrendDown,
    Minus,
    Star,
    Crown,
    Medal,
    Users2,
    CalendarClock,
    MapPinned,
    Smartphone as DeviceMobile,
    Laptop,
    Gauge,
    Flame,
    Timer,
    ScrollText,
    ArrowUpRight,
    ArrowDownRight,
    Info,
    DollarSign
} from "lucide-react";

const CampaignAnalytics = () => {
    // ============== STATE MANAGEMENT ==============
    const [campaigns, setCampaigns] = useState([]);
    const [filteredCampaigns, setFilteredCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [error, setError] = useState("");
    const [analyticsError, setAnalyticsError] = useState("");
    const [summaryError, setSummaryError] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState("7d");
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [currentPage, setCurrentPage] = useState(1);
    const [showAnalyticsView, setShowAnalyticsView] = useState(false);
    const [hoveredMetric, setHoveredMetric] = useState(null);
    const [sortBy, setSortBy] = useState("newest");
    const [viewMode, setViewMode] = useState("grid");
    const [dateRange, setDateRange] = useState({ from: "", to: "" });
    const { showLoader, hideLoader } = useLoader();
    const itemsPerPage = 8;

    // ============== CONSTANTS ==============
    const statusOptions = ["ALL", "DRAFT", "SCHEDULED", "ACTIVE", "PAUSED", "COMPLETED", "CANCELLED"];

    const statusColors = {
        'DRAFT': { bg: '#9E9E9E', light: '#F5F5F5', text: '#757575' },
        'SCHEDULED': { bg: '#FF9800', light: '#FFF3E0', text: '#F57C00' },
        'ACTIVE': { bg: '#4CAF50', light: '#E8F5E9', text: '#388E3C' },
        'PAUSED': { bg: '#FFC107', light: '#FFF8E1', text: '#FFA000' },
        'COMPLETED': { bg: '#2196F3', light: '#E3F2FD', text: '#1976D2' },
        'CANCELLED': { bg: '#f44336', light: '#FFEBEE', text: '#D32F2F' }
    };

    const metricColors = {
        views: { primary: '#3B82F6', secondary: '#93C5FD', gradient: 'linear-gradient(135deg, #3B82F6, #1D4ED8)' },
        clicks: { primary: '#10B981', secondary: '#6EE7B7', gradient: 'linear-gradient(135deg, #10B981, #047857)' },
        engagement: { primary: '#8B5CF6', secondary: '#C4B5FD', gradient: 'linear-gradient(135deg, #8B5CF6, #6D28D9)' },
        conversions: { primary: '#F59E0B', secondary: '#FCD34D', gradient: 'linear-gradient(135deg, #F59E0B, #B45309)' },
        likes: { primary: '#EC4899', secondary: '#F9A8D4', gradient: 'linear-gradient(135deg, #EC4899, #BE185D)' },
        shares: { primary: '#6366F1', secondary: '#C7D2FE', gradient: 'linear-gradient(135deg, #6366F1, #4338CA)' },
        profit: { primary: '#10B981', secondary: '#A7F3D0', gradient: 'linear-gradient(135deg, #10B981, #047857)' }
    };

    // Helper function to format date for API
    const formatDateForAPI = (dateStr) => {
        if (!dateStr) return "";
        return `${dateStr}T00:00:00`;
    };

    // ============== API FUNCTIONS ==============
    useEffect(() => {
        fetchCampaigns();
    }, []);

    // Fetch summary when both dates are selected
    useEffect(() => {
        if (dateRange.from && dateRange.to) {
            fetchSummaryAnalytics();
        } else {
            setSummaryData(null);
        }
    }, [dateRange.from, dateRange.to]);

    useEffect(() => {
        let filtered = [...campaigns];

        if (searchTerm) {
            filtered = filtered.filter(campaign =>
                campaign.campaignName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                campaign.id?.toString().includes(searchTerm) ||
                campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter !== "ALL") {
            filtered = filtered.filter(campaign => campaign.status === statusFilter);
        }

        if (sortBy === "newest") {
            filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        } else if (sortBy === "oldest") {
            filtered.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        } else if (sortBy === "name") {
            filtered.sort((a, b) => (a.campaignName || "").localeCompare(b.campaignName || ""));
        }

        setFilteredCampaigns(filtered);
        setCurrentPage(1);
    }, [searchTerm, statusFilter, sortBy, campaigns]);

    const fetchCampaigns = async () => {
        try {
            setLoading(true);
            showLoader();
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `https://backend.maamaas.com/promotions/api/admin/all/campaigns`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const campaignsArray = Array.isArray(response.data) ? response.data : [response.data];
            setCampaigns(campaignsArray);
            setFilteredCampaigns(campaignsArray);
            setError("");
        } catch (error) {
            console.error("❌ Error fetching campaigns:", error);
            setError("Failed to fetch campaigns");
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    const fetchSummaryAnalytics = async () => {
        try {
            setLoadingSummary(true);
            setSummaryError("");

            const token = localStorage.getItem("authToken");

            const response = await axios.get(
                `https://backend.maamaas.com/promotions/api/admin/campaigns/analytics`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        fromDate: formatDateForAPI(dateRange.from),
                        toDate: formatDateForAPI(dateRange.to)
                    }
                }
            );

            setSummaryData(response.data);
        } catch (err) {
            console.error("❌ Summary Analytics Error:", err);
            setSummaryError(err.response?.data?.message || "Failed to load summary");
        } finally {
            setLoadingSummary(false);
        }
    };

    const fetchAnalytics = async (campaign) => {
        try {
            setLoadingAnalytics(true);
            setAnalyticsError("");
            showLoader();
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `https://backend.maamaas.com/promotions/api/user/campaigns/${campaign.id}/stats`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setAnalyticsData({
                ...campaign,
                ...response.data,
                campaignName: campaign.campaignName || response.data.campaignName,
                campaignId: campaign.id || response.data.campaignId
            });
            setShowAnalyticsView(true);
        } catch (err) {
            console.error("❌ Analytics API Error:", err);
            let errorMessage = "Failed to load analytics";
            if (err.response) {
                errorMessage = err.response.data?.message || err.response.data?.error || `Server error: ${err.response.status}`;
            } else if (err.request) {
                errorMessage = "No response from server. Please check your connection.";
            } else {
                errorMessage = err.message;
            }
            setAnalyticsError(errorMessage);
        } finally {
            setLoadingAnalytics(false);
            hideLoader();
        }
    };

    // ============== HANDLERS ==============
    const handleSelectCampaign = (campaign) => {
        setSelectedCampaign(campaign);
        fetchAnalytics(campaign);
    };

    const handleBackToList = () => {
        setShowAnalyticsView(false);
        setSelectedCampaign(null);
        setAnalyticsData(null);
        setAnalyticsError("");
    };

    const handleDateChange = (type, value) => {
        setDateRange(prev => ({
            ...prev,
            [type]: value
        }));
    };

    // ============== EXPORT FUNCTIONS ==============
    const exportToCSV = () => {
        if (!analyticsData) return;
        const headers = ['Metric', 'Value', 'Period'];
        const rows = [
            ['Campaign Name', analyticsData.campaignName, selectedPeriod],
            ['Campaign ID', analyticsData.id, selectedPeriod],
            ['Total Delivered', analyticsData.totalDelivered || 0, selectedPeriod],
            ['Total Views', analyticsData.totalViews || 0, selectedPeriod],
            ['Unique Viewers', analyticsData.uniqueViewers || 0, selectedPeriod],
            ['Unique Clickers', analyticsData.uniqueClickers || 0, selectedPeriod],
            ['Total Likes', analyticsData.totalLikes || 0, selectedPeriod],
            ['Total Shares', analyticsData.totalShares || 0, selectedPeriod],
            ['Total Dismissals', analyticsData.totalDismissals || 0, selectedPeriod],
            ['View Rate (%)', analyticsData.viewRate || 0, selectedPeriod],
            ['Like Rate (%)', analyticsData.likeRate || 0, selectedPeriod],
            ['Avg View Duration (s)', analyticsData.avgViewDuration || 0, selectedPeriod],
            ['Avg Scroll Depth (%)', analyticsData.avgScrollDepth || 0, selectedPeriod]
        ];
        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `campaign_analytics_${analyticsData.id}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        setShowExportMenu(false);
    };

    const exportToJSON = () => {
        if (!analyticsData) return;
        const exportData = {
            campaign: { id: analyticsData.id, name: analyticsData.campaignName },
            analytics: analyticsData,
            exportedAt: new Date().toISOString(),
            period: selectedPeriod
        };
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `campaign_analytics_${analyticsData.id}_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
        setShowExportMenu(false);
    };

    // ============== HELPER FUNCTIONS ==============
    const calculateMetrics = () => {
        if (!analyticsData) return {};
        const totalViews = analyticsData.totalViews || 0;
        const totalLikes = analyticsData.totalLikes || 0;
        const totalShares = analyticsData.totalShares || 0;
        const uniqueViewers = analyticsData.uniqueViewers || 1;
        const totalDelivered = analyticsData.totalDelivered || 1;
        const totalViews_safe = totalViews || 1;

        const engagementRate = totalViews > 0 ? ((totalLikes + totalShares) / totalViews * 100).toFixed(1) : '0.0';
        const clickThroughRate = analyticsData.uniqueClickers && uniqueViewers > 0 ? (analyticsData.uniqueClickers / uniqueViewers * 100).toFixed(1) : '0.0';
        const dismissalRate = totalDelivered > 0 ? ((analyticsData.totalDismissals || 0) / totalDelivered * 100).toFixed(1) : '0.0';
        const conversionRate = totalViews_safe > 0 ? ((analyticsData.uniqueClickers || 0) / totalViews_safe * 100).toFixed(1) : '0.0';
        const likeToViewRatio = totalViews > 0 ? ((totalLikes / totalViews) * 100).toFixed(1) : '0.0';
        const shareToViewRatio = totalViews > 0 ? ((totalShares / totalViews) * 100).toFixed(1) : '0.0';

        return {
            engagementRate,
            clickThroughRate,
            dismissalRate,
            conversionRate,
            likeToViewRatio,
            shareToViewRatio
        };
    };

    const getStatusColor = (status) => statusColors[status]?.bg || '#9E9E9E';
    const getStatusLightColor = (status) => statusColors[status]?.light || '#F5F5F5';
    const getStatusTextColor = (status) => statusColors[status]?.text || '#757575';

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleString("en-GB", {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const getTrendIcon = (value) => {
        if (value > 0) return <ArrowUpRight size={14} color="#10B981" />;
        if (value < 0) return <ArrowDownRight size={14} color="#EF4444" />;
        return <Minus size={14} color="#9CA3AF" />;
    };

    // ============== PAGINATION ==============
    const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedCampaigns = filteredCampaigns.slice(startIndex, startIndex + itemsPerPage);

    // ============== MODERN COMPONENTS ==============
    const ModernMetricCard = ({ title, value, icon: Icon, trend, trendValue, color = "views", subtitle, tooltip }) => {
        const colors = metricColors[color] || metricColors.views;

        return (
            <div
                style={styles.modernMetricCard}
                onMouseEnter={() => setHoveredMetric(title)}
                onMouseLeave={() => setHoveredMetric(null)}
            >
                <div style={styles.modernMetricHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            padding: '8px',
                            borderRadius: '10px',
                            background: colors.secondary,
                            color: colors.primary
                        }}>
                            <Icon size={18} />
                        </div>
                        <span style={styles.modernMetricTitle}>
                            {title}
                            {tooltip && <Info size={12} style={{ marginLeft: '4px', cursor: 'help' }} title={tooltip} />}
                        </span>
                    </div>
                    {trend && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '20px',
                            background: trend > 0 ? '#D1FAE5' : trend < 0 ? '#FEE2E2' : '#F3F4F6'
                        }}>
                            {getTrendIcon(trend)}
                            <span style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: trend > 0 ? '#059669' : trend < 0 ? '#DC2626' : '#6B7280'
                            }}>
                                {trend > 0 ? '+' : ''}{trendValue}
                            </span>
                        </div>
                    )}
                </div>
                <div style={styles.modernMetricValue}>
                    {value}
                    {subtitle && <span style={styles.modernMetricSubtitle}>{subtitle}</span>}
                </div>
                <div style={styles.modernMetricProgress}>
                    <div style={styles.progressBarBg}>
                        <div style={{
                            ...styles.progressBarFill,
                            width: hoveredMetric === title ? '100%' : '70%',
                            background: colors.gradient,
                        }} />
                    </div>
                </div>
            </div>
        );
    };

    const ModernStatCard = ({ label, value, icon: Icon, change, bgColor = '#F3F4F6', iconColor = '#6B7280' }) => (
        <div style={styles.modernStatCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                    padding: '10px',
                    borderRadius: '12px',
                    background: bgColor,
                    color: iconColor
                }}>
                    <Icon size={20} />
                </div>
                <div>
                    <div style={styles.statLabel}>{label}</div>
                    <div style={styles.statValue}>{value}</div>
                </div>
            </div>
            {change && (
                <div style={{
                    ...styles.trendBadge,
                    background: change > 0 ? '#D1FAE5' : '#FEE2E2',
                    color: change > 0 ? '#059669' : '#DC2626'
                }}>
                    {change > 0 ? '+' : ''}{change}%
                </div>
            )}
        </div>
    );

    const ProgressRing = ({ percentage, size = 60, color = "#3B82F6", label }) => {
        const strokeWidth = 4;
        const radius = (size - strokeWidth) / 2;
        const circumference = radius * 2 * Math.PI;
        const offset = circumference - (percentage / 100) * circumference;

        return (
            <div style={{ textAlign: 'center' }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="#E5E7EB"
                        strokeWidth={strokeWidth}
                    />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dashoffset 0.5s' }}
                    />
                </svg>
                {label && <div style={styles.ringLabel}>{label}</div>}
            </div>
        );
    };

    const TimelineItem = ({ icon: Icon, title, time, status, color = "#3B82F6" }) => (
        <div style={styles.timelineItem}>
            <div style={{
                ...styles.timelineIcon,
                background: `${color}15`,
                color: color
            }}>
                <Icon size={16} />
            </div>
            <div style={{ flex: 1 }}>
                <div style={styles.timelineTitle}>{title}</div>
                <div style={styles.timelineTime}>{time}</div>
            </div>
            {status && (
                <span style={{
                    ...styles.statusBadgeSmall,
                    background: statusColors[status]?.light || '#F3F4F6',
                    color: statusColors[status]?.text || '#6B7280'
                }}>
                    {status}
                </span>
            )}
        </div>
    );

    // ============== MAIN RENDER ==============
    return (
        <div style={styles.container}>
            {/* Global Styles */}
            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideIn { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                .fade-in { animation: fadeIn 0.5s ease-out forwards; }
                .slide-in { animation: slideIn 0.3s ease-out forwards; }
                .hover-scale { transition: transform 0.2s ease; }
                .hover-scale:hover { transform: scale(1.02); }
                .glass-effect { background: rgba(255, 255, 255, 0.8); backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.3); }
            `}</style>

            {/* Header with Date Range */}
            <div style={styles.header}>

                <div style={styles.headerRight}>
                    <div style={styles.dateRangePicker}>
                        <div style={styles.dateInput}>
                            {/* <Calendar size={14} color="#6B7280" /> */}
                            <input
                                type="date"
                                value={dateRange.from}
                                onChange={(e) => handleDateChange('from', e.target.value)}
                                style={styles.dateField}
                                max={dateRange.to || undefined}
                                placeholder="From"
                            />
                        </div>
                        <span style={styles.dateSeparator}>to</span>
                        <div style={styles.dateInput}>
                            {/* <Calendar size={14} color="#6B7280" /> */}
                            <input
                                type="date"
                                value={dateRange.to}
                                onChange={(e) => handleDateChange('to', e.target.value)}
                                style={styles.dateField}
                                min={dateRange.from || undefined}
                                max={new Date().toISOString().split('T')[0]}
                                placeholder="To"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Date Selection Prompt */}
            {(!dateRange.from || !dateRange.to) && (
                <div style={styles.promptContainer}>
                    {/* <AlertCircle size={20} color="#F59E0B" />
                    <span style={styles.promptText}>Please select both From and To dates to view summary analytics</span> */}
                </div>
            )}

            {/* Summary Analytics Cards - Only shown when both dates selected */}
            {dateRange.from && dateRange.to && (
                <>
                    {loadingSummary ? (
                        <div style={styles.loadingSummary}>
                            <div style={styles.spinner} />
                            <p>Loading summary data...</p>
                        </div>
                    ) : summaryError ? (
                        <div style={styles.errorSummary}>
                            <AlertTriangle size={24} color="#EF4444" />
                            <p>{summaryError}</p>
                        </div>
                    ) : summaryData && (
                        <div style={styles.summaryGrid}>
                            <ModernMetricCard
                                title="Total Profit"
                                value={formatCurrency(summaryData.profit)}
                                icon={DollarSign}
                                color="profit"
                                tooltip="Total profit for selected period"
                                subtitle={`${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`}
                            />
                            <ModernMetricCard
                                title="Active Campaigns"
                                value={summaryData.activeCampaigns}
                                icon={Activity}
                                color="engagement"
                                tooltip="Number of active campaigns in this period"
                            />
                            <ModernMetricCard
                                title="Total Campaigns"
                                value={summaryData.totalCampaigns}
                                icon={Briefcase}
                                color="views"
                                tooltip="Total campaigns in this period"
                            />
                        </div>
                    )}
                </>
            )}

            {!showAnalyticsView ? (
                /* Campaign List View */
                <div style={styles.listContainer} className="glass-effect">
                    {/* Filters */}
                    <div style={styles.filters}>
                        <div style={styles.searchContainer}>
                            <Search size={18} color="#9CA3AF" />
                            <input
                                type="text"
                                placeholder="Search campaigns..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={styles.searchInput}
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} style={styles.clearButton}>
                                    <XCircle size={16} color="#9CA3AF" />
                                </button>
                            )}
                        </div>

                        <div style={styles.filterGroup}>
                            <div style={styles.filterSelect}>
                                <Filter size={16} color="#6B7280" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={styles.select}
                                >
                                    {statusOptions.map(status => (
                                        <option key={status} value={status}>
                                            {status === "ALL" ? "All Campaigns" : status}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={styles.filterSelect}>
                                <Layers size={16} color="#6B7280" />
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    style={styles.select}
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="name">Name A-Z</option>
                                </select>
                            </div>

                            <div style={styles.viewToggle}>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    style={{
                                        ...styles.viewButton,
                                        background: viewMode === 'grid' ? 'rgb(230 109 51)' : 'transparent',
                                        color: viewMode === 'grid' ? 'white' : '#6B7280'
                                    }}
                                >
                                    <Layers size={16} />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    style={{
                                        ...styles.viewButton,
                                        background: viewMode === 'list' ? 'rgb(230 109 51)' : 'transparent',
                                        color: viewMode === 'list' ? 'white' : '#6B7280'
                                    }}
                                >
                                    <Briefcase size={16} />
                                </button>
                            </div>

                            <button onClick={fetchCampaigns} style={styles.refreshButton}>
                                <RefreshCw size={16} className={loading ? 'spin' : ''} />
                            </button>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    {!loading && !error && campaigns.length > 0 && (
                        <div style={styles.quickStats}>
                            <ModernStatCard
                                label="Total Campaigns"
                                value={campaigns.length}
                                icon={Briefcase}
                                bgColor="#EFF6FF"
                                iconColor="#3B82F6"
                            />
                            <ModernStatCard
                                label="Active Now"
                                value={campaigns.filter(c => c.status === 'ACTIVE').length}
                                icon={Flame}
                                bgColor="#FEF3C7"
                                iconColor="#F59E0B"
                            />
                            <ModernStatCard
                                label="Completed"
                                value={campaigns.filter(c => c.status === 'COMPLETED').length}
                                icon={CheckCircle}
                                bgColor="#D1FAE5"
                                iconColor="#10B981"
                            />
                            <ModernStatCard
                                label="Scheduled"
                                value={campaigns.filter(c => c.status === 'SCHEDULED').length}
                                icon={CalendarClock}
                                bgColor="#F3E8FF"
                                iconColor="#8B5CF6"
                            />
                        </div>
                    )}

                    {/* Campaign Grid/List */}
                    {loading ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.spinner} />
                            <p style={{ marginTop: '20px', color: '#6B7280' }}>Loading your campaigns...</p>
                        </div>
                    ) : error ? (
                        <div style={styles.errorContainer}>
                            <AlertTriangle size={48} color="#EF4444" />
                            <p style={{ marginTop: '16px', color: '#EF4444', fontWeight: 500 }}>{error}</p>
                            <button onClick={fetchCampaigns} style={styles.retryButton}>
                                <RefreshCw size={16} style={{ marginRight: '8px' }} />
                                Try Again
                            </button>
                        </div>
                    ) : filteredCampaigns.length === 0 ? (
                        <div style={styles.emptyContainer}>
                            <Briefcase size={48} color="#9CA3AF" />
                            <p style={{ marginTop: '16px', color: '#4B5563', fontWeight: 500 }}>No campaigns found</p>
                            <p style={{ color: '#9CA3AF', fontSize: '14px' }}>
                                {searchTerm ? 'Try adjusting your filters' : 'Create your first campaign'}
                            </p>
                            {searchTerm && (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('ALL');
                                    }}
                                    style={styles.clearAllButton}
                                >
                                    Clear All Filters
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div style={viewMode === 'grid' ? styles.gridContainer : styles.listContainer}>
                                {paginatedCampaigns.map((campaign, index) => (
                                    <div
                                        key={campaign.id}
                                        style={{
                                            ...(viewMode === 'grid' ? styles.gridCard : styles.listItem),
                                            animation: `fadeIn 0.3s ease-out ${index * 0.05}s forwards`,
                                            opacity: 0
                                        }}
                                        onClick={() => handleSelectCampaign(campaign)}
                                        className="hover-scale"
                                    >
                                        {viewMode === 'grid' ? (

                                            <>
                                                <div style={styles.cardHeader}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{
                                                            ...styles.cardIcon,
                                                            background: getStatusLightColor(campaign.status)
                                                        }}>
                                                            <Target size={16} color={getStatusColor(campaign.status)} />
                                                        </div>
                                                        <h3 style={styles.cardTitle}>{campaign.campaignName || 'Untitled'}</h3>
                                                    </div>
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        background: getStatusLightColor(campaign.status),
                                                        color: getStatusTextColor(campaign.status)
                                                    }}>
                                                        {campaign.status || 'DRAFT'}
                                                    </span>
                                                </div>

                                                <p style={styles.cardDescription}>
                                                    {campaign.description || 'No description'}
                                                </p>

                                                <div style={styles.cardDetails}>
                                                    <div style={styles.cardDetail}>
                                                        <Calendar size={12} color="#9CA3AF" />
                                                        <span>{formatDate(campaign.startDate)}</span>
                                                    </div>
                                                </div>

                                                {campaign.interests?.length > 0 && (
                                                    <div style={styles.interestsContainer}>
                                                        {campaign.interests.slice(0, 2).map((interest, idx) => (
                                                            <span key={idx} style={styles.interestBadge}>
                                                                {interest}
                                                            </span>
                                                        ))}
                                                        {campaign.interests.length > 2 && (
                                                            <span style={styles.moreBadge}>+{campaign.interests.length - 2}</span>
                                                        )}
                                                    </div>
                                                )}

                                                <button style={styles.viewDetailsButton}>
                                                    <Eye size={14} />
                                                    View Analytics
                                                    <ArrowUpRight size={14} />
                                                </button>
                                            </>
                                        ) : (
                                            /* List View */
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                                                <div style={{
                                                    ...styles.listIcon,
                                                    background: getStatusLightColor(campaign.status)
                                                }}>
                                                    <Target size={20} color={getStatusColor(campaign.status)} />
                                                </div>

                                                <div style={{ flex: 1 }}>
                                                    <div style={styles.listTitle}>
                                                        {campaign.campaignName || 'Untitled Campaign'}
                                                    </div>
                                                    <div style={styles.listSubtitle}>
                                                        ID: {campaign.id}
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                                    <div>
                                                        <div style={styles.listLabel}>Start Date</div>
                                                        <div style={styles.listValue}>{formatDate(campaign.startDate)}</div>
                                                    </div>

                                                    <div>
                                                        <div style={styles.listLabel}>Location</div>
                                                        <div style={styles.listValue}>{campaign.city || 'All'}</div>
                                                    </div>

                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        background: getStatusLightColor(campaign.status),
                                                        color: getStatusTextColor(campaign.status)
                                                    }}>
                                                        {campaign.status || 'DRAFT'}
                                                    </span>

                                                    <button style={styles.iconButton}>
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div style={styles.pagination}>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        style={styles.pageButton}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>

                                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={i}
                                                onClick={() => setCurrentPage(pageNum)}
                                                style={{
                                                    ...styles.pageButton,
                                                    background: currentPage === pageNum ? 'rgb(230 109 51)' : 'transparent',
                                                    color: currentPage === pageNum ? 'white' : '#4B5563',
                                                    borderColor: currentPage === pageNum ? 'rgb(230 109 51)' : '#E5E7EB'
                                                }}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        style={styles.pageButton}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                /* Analytics View */
                <div style={styles.analyticsContainer} className="glass-effect">
                    {/* Back Button */}
                    <button onClick={handleBackToList} style={styles.backButton}>
                        <ChevronLeft size={18} />
                        Back to Campaigns
                    </button>

                    {/* Campaign Header */}
                    <div style={styles.analyticsHeader}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <h2 style={styles.analyticsTitle}>{selectedCampaign?.campaignName}</h2>
                                <span style={{
                                    ...styles.statusBadge,
                                    background: getStatusLightColor(selectedCampaign?.status),
                                    color: getStatusTextColor(selectedCampaign?.status),
                                    padding: '4px 12px'
                                }}>
                                    {selectedCampaign?.status || 'DRAFT'}
                                </span>
                            </div>
                            <div style={styles.campaignMeta}>
                                <div style={styles.metaItem}>
                                    <Calendar size={14} color="#9CA3AF" />
                                    <span>Created: {formatDate(selectedCampaign?.createdAt)}</span>
                                </div>
                                <div style={styles.metaItem}>
                                    <Users size={14} color="#9CA3AF" />
                                    <span>ID: {selectedCampaign?.id}</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            {/* Export Button */}
                            <div>
                                <button
                                    onClick={exportToCSV}
                                    style={styles.exportButton}
                                >
                                    <Download size={16} />
                                    Export CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Analytics Content */}
                    {loadingAnalytics ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.spinner} />
                            <p style={{ marginTop: '20px', color: '#6B7280' }}>Loading analytics...</p>
                        </div>
                    ) : analyticsError ? (
                        <div style={styles.errorContainer}>
                            <AlertTriangle size={48} color="#EF4444" />
                            <p style={{ marginTop: '16px', color: '#EF4444' }}>{analyticsError}</p>
                            <button onClick={() => fetchAnalytics(selectedCampaign)} style={styles.retryButton}>
                                <RefreshCw size={16} style={{ marginRight: '8px' }} />
                                Retry
                            </button>
                        </div>
                    ) : analyticsData ? (
                        <>
                            {/* Key Metrics Grid */}
                            <div style={styles.metricsGrid}>
                                <ModernMetricCard
                                    title="Total Delivered"
                                    value={formatNumber(analyticsData.totalDelivered)}
                                    icon={Target}
                                    tooltip="Number of times campaign was delivered"
                                />
                                <ModernMetricCard
                                    title="Total Views"
                                    value={formatNumber(analyticsData.totalViews)}
                                    icon={Eye}
                                    tooltip="Total number of views"
                                />
                                <ModernMetricCard
                                    title="Unique Viewers"
                                    value={formatNumber(analyticsData.uniqueViewers)}
                                    icon={Users}
                                    tooltip="Unique users who viewed"
                                />
                                <ModernMetricCard
                                    title="Engagement Rate"
                                    value={`${calculateMetrics().engagementRate}%`}
                                    icon={Activity}
                                    tooltip="(Likes + Shares) / Views"
                                />
                            </div>

                            {/* Main Analytics Grid */}
                            <div style={styles.analyticsGrid}>
                                {/* Interactions Card */}
                                <div style={styles.analyticsCard}>
                                    <div style={styles.cardHeader}>
                                        <Zap size={18} color="#8B5CF6" />
                                        <h3 style={styles.cardTitle}>User Interactions</h3>
                                    </div>

                                    <div style={{ marginTop: '16px' }}>
                                        <div style={styles.interactionItem}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ padding: '8px', background: '#DBEAFE', borderRadius: '8px' }}>
                                                    <MousePointer size={16} color="#3B82F6" />
                                                </div>
                                                <div>
                                                    <div style={styles.interactionLabel}>Unique Clickers</div>
                                                    <div style={styles.interactionValue}>
                                                        {formatNumber(analyticsData.uniqueClickers)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={styles.interactionItem}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ padding: '8px', background: '#FCE7F3', borderRadius: '8px' }}>
                                                    <Heart size={16} color="#EC4899" />
                                                </div>
                                                <div>
                                                    <div style={styles.interactionLabel}>Total Likes</div>
                                                    <div style={styles.interactionValue}>
                                                        {formatNumber(analyticsData.totalLikes)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={styles.interactionItem}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ padding: '8px', background: '#D1FAE5', borderRadius: '8px' }}>
                                                    <Share2 size={16} color="#10B981" />
                                                </div>
                                                <div>
                                                    <div style={styles.interactionLabel}>Total Shares</div>
                                                    <div style={styles.interactionValue}>
                                                        {formatNumber(analyticsData.totalShares)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={styles.interactionItem}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ padding: '8px', background: '#FEE2E2', borderRadius: '8px' }}>
                                                    <XCircle size={16} color="#EF4444" />
                                                </div>
                                                <div>
                                                    <div style={styles.interactionLabel}>Total Dismissals</div>
                                                    <div style={styles.interactionValue}>
                                                        {formatNumber(analyticsData.totalDismissals)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Rate Metrics Card */}
                                <div style={styles.analyticsCard}>
                                    <div style={styles.cardHeader}>
                                        <Gauge size={18} color="#F59E0B" />
                                        <h3 style={styles.cardTitle}>Performance Rates</h3>
                                    </div>

                                    <div style={{ marginTop: '16px' }}>
                                        <div style={styles.rateItem}>
                                            <div style={styles.rateHeader}>
                                                <span style={styles.rateLabel}>View Rate</span>
                                                <span style={styles.rateValue}>{analyticsData.viewRate || 0}%</span>
                                            </div>
                                            <div style={styles.progressBarBg}>
                                                <div style={{
                                                    ...styles.progressBarFill,
                                                    width: `${analyticsData.viewRate || 0}%`,
                                                    background: '#3B82F6'
                                                }} />
                                            </div>
                                        </div>

                                        <div style={styles.rateItem}>
                                            <div style={styles.rateHeader}>
                                                <span style={styles.rateLabel}>Like Rate</span>
                                                <span style={{ ...styles.rateValue, color: '#EC4899' }}>
                                                    {analyticsData.likeRate || 0}%
                                                </span>
                                            </div>
                                            <div style={styles.progressBarBg}>
                                                <div style={{
                                                    ...styles.progressBarFill,
                                                    width: `${analyticsData.likeRate || 0}%`,
                                                    background: '#EC4899'
                                                }} />
                                            </div>
                                        </div>

                                        <div style={styles.rateItem}>
                                            <div style={styles.rateHeader}>
                                                <span style={styles.rateLabel}>Click-Through Rate</span>
                                                <span style={{ ...styles.rateValue, color: '#10B981' }}>
                                                    {calculateMetrics().clickThroughRate}%
                                                </span>
                                            </div>
                                            <div style={styles.progressBarBg}>
                                                <div style={{
                                                    ...styles.progressBarFill,
                                                    width: `${calculateMetrics().clickThroughRate}%`,
                                                    background: '#10B981'
                                                }} />
                                            </div>
                                        </div>

                                        <div style={styles.rateItem}>
                                            <div style={styles.rateHeader}>
                                                <span style={styles.rateLabel}>Dismissal Rate</span>
                                                <span style={{ ...styles.rateValue, color: '#F59E0B' }}>
                                                    {calculateMetrics().dismissalRate}%
                                                </span>
                                            </div>
                                            <div style={styles.progressBarBg}>
                                                <div style={{
                                                    ...styles.progressBarFill,
                                                    width: `${calculateMetrics().dismissalRate}%`,
                                                    background: '#F59E0B'
                                                }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Performance Card */}
                                <div style={styles.analyticsCard}>
                                    <div style={styles.cardHeader}>
                                        <Crown size={18} color="#F59E0B" />
                                        <h3 style={styles.cardTitle}>Key Indicators</h3>
                                    </div>

                                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                            <ProgressRing
                                                percentage={parseFloat(calculateMetrics().conversionRate)}
                                                size={100}
                                                color="#8B5CF6"
                                            />
                                            <div style={styles.ringCenter}>
                                                <div style={styles.ringPercentage}>
                                                    {calculateMetrics().conversionRate}%
                                                </div>
                                                <div style={styles.ringLabel}>Conversion</div>
                                            </div>
                                        </div>

                                        <div style={styles.indicatorGrid}>
                                            <div style={styles.indicator}>
                                                <Timer size={16} color="#6B7280" />
                                                <div>
                                                    <div style={styles.indicatorLabel}>Avg Duration</div>
                                                    <div style={styles.indicatorValue}>
                                                        {analyticsData.avgViewDuration || 0}s
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={styles.indicator}>
                                                <ScrollText size={16} color="#6B7280" />
                                                <div>
                                                    <div style={styles.indicatorLabel}>Scroll Depth</div>
                                                    <div style={styles.indicatorValue}>
                                                        {analyticsData.avgScrollDepth || 0}%
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={styles.ratiosGrid}>
                                            <div>
                                                <div style={styles.ratioLabel}>Like/View</div>
                                                <div style={styles.ratioValue}>
                                                    {calculateMetrics().likeToViewRatio}%
                                                </div>
                                            </div>
                                            <div>
                                                <div style={styles.ratioLabel}>Share/View</div>
                                                <div style={styles.ratioValue}>
                                                    {calculateMetrics().shareToViewRatio}%
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Timeline & Targeting */}
                            <div style={styles.detailsGrid}>
                                <div style={styles.detailsCard}>
                                    <h4 style={styles.detailsTitle}>
                                        <CalendarClock size={16} color="#8B5CF6" />
                                        Campaign Timeline
                                    </h4>
                                    <div style={styles.timeline}>
                                        <TimelineItem
                                            icon={Calendar}
                                            title="Campaign Started"
                                            time={formatDateTime(selectedCampaign?.startDate)}
                                            color="#10B981"
                                        />
                                        <TimelineItem
                                            icon={Clock}
                                            title="Campaign Ends"
                                            time={formatDateTime(selectedCampaign?.endDate)}
                                            status={selectedCampaign?.status}
                                        />
                                    </div>
                                </div>

                                <div style={styles.detailsCard}>
                                    <h4 style={styles.detailsTitle}>
                                        <MapPinned size={16} color="#8B5CF6" />
                                        Targeting Details
                                    </h4>
                                    <div style={styles.targetingGrid}>
                                        <div>
                                            <div style={styles.targetingLabel}>Location</div>
                                            <div style={styles.targetingValue}>
                                                {selectedCampaign?.city || 'All Cities'}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={styles.targetingLabel}>Radius</div>
                                            <div style={styles.targetingValue}>
                                                {selectedCampaign?.radiusKm || 10} km
                                            </div>
                                        </div>
                                        <div>
                                            <div style={styles.targetingLabel}>Platform</div>
                                            <div style={{ ...styles.targetingValue, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                {selectedCampaign?.medium === 'APP' ? <DeviceMobile size={14} /> : <Laptop size={14} />}
                                                {selectedCampaign?.medium || 'APP'}
                                            </div>
                                        </div>
                                    </div>

                                    {selectedCampaign?.interests?.length > 0 && (
                                        <div style={{ marginTop: '16px' }}>
                                            <div style={styles.targetingLabel}>Target Interests</div>
                                            <div style={styles.interestsGrid}>
                                                {selectedCampaign.interests.map((interest, idx) => (
                                                    <span key={idx} style={styles.interestBadge}>
                                                        {interest}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Success Metrics */}
                            <div style={styles.successMetrics}>
                                <div style={styles.successItem}>
                                    <CheckCircle size={20} color="#10B981" />
                                    <div>
                                        <div style={styles.successLabel}>Success Rate</div>
                                        <div style={styles.successValue}>
                                            {analyticsData.totalViews && analyticsData.totalDelivered
                                                ? ((analyticsData.totalViews / analyticsData.totalDelivered) * 100).toFixed(1)
                                                : 0}%
                                        </div>
                                    </div>
                                </div>
                                <div style={styles.successItem}>
                                    <Medal size={20} color="#F59E0B" />
                                    <div>
                                        <div style={styles.successLabel}>Engagement Score</div>
                                        <div style={styles.successValue}>
                                            {((analyticsData.totalLikes + analyticsData.totalShares) / (analyticsData.totalViews || 1) * 10).toFixed(1)}
                                        </div>
                                    </div>
                                </div>
                                <div style={styles.successItem}>
                                    <Star size={20} color="#8B5CF6" />
                                    <div>
                                        <div style={styles.successLabel}>Viral Score</div>
                                        <div style={styles.successValue}>
                                            {analyticsData.totalShares && analyticsData.uniqueViewers
                                                ? (analyticsData.totalShares / analyticsData.uniqueViewers).toFixed(2)
                                                : 0}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Refresh Button */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                                <button
                                    onClick={() => fetchAnalytics(selectedCampaign)}
                                    style={styles.refreshAnalyticsButton}
                                >
                                    <RefreshCw size={16} style={{ marginRight: '8px' }} />
                                    Refresh Analytics
                                </button>
                            </div>
                        </>
                    ) : null}
                </div>
            )}
        </div>
    );
};

// ============== STYLES ==============
const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: '#F9FAFB',
        height: "90vh",
        scrollbarWidth: "none",
        overflow: "auto"
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
    },
    headerIcon: {
        padding: '12px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgb(230 109 51), #8E44AD)',
        boxShadow: '0 8px 16px rgba(177, 92, 214, 0.3)'
    },
    title: {
        fontSize: '28px',
        fontWeight: 700,
        margin: 0,
        background: 'linear-gradient(135deg, rgb(230 109 51), #8E44AD)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    },
    subtitle: {
        fontSize: '14px',
        color: '#6B7280',
        margin: '4px 0 0 0'
    },
    headerRight: {
        display: 'flex',
        gap: '12px'
    },
    dateRangePicker: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 12px',
        background: 'white',
        borderRadius: '30px',
        border: '1px solid #E5E7EB'
    },
    dateInput: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    dateField: {
        border: 'none',
        outline: 'none',
        fontSize: '13px',
        color: '#4B5563',
        padding: '6px',
        borderRadius: '4px',
        width: '130px'
    },
    dateSeparator: {
        color: '#9CA3AF',
        fontSize: '13px'
    },
    // promptContainer: {
    //     display: 'flex',
    //     alignItems: 'center',
    //     gap: '12px',
    //     padding: '16px 20px',
    //     background: '#FEF3C7',
    //     borderRadius: '16px',
    //     marginBottom: '24px'
    // },
    // promptText: {
    //     color: '#92400E',
    //     fontSize: '14px',
    //     fontWeight: 500
    // },
    loadingSummary: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        padding: '40px',
        background: 'white',
        borderRadius: '24px',
        marginBottom: '24px'
    },
    errorSummary: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '20px',
        background: '#FEF2F2',
        borderRadius: '16px',
        marginBottom: '24px'
    },
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '24px'
    },
    listContainer: {
        background: 'white',
        borderRadius: '24px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
    },
    filters: {
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap'
    },
    searchContainer: {
        flex: 2,
        display: 'flex',
        alignItems: 'center',
        padding: '8px 16px',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '30px',
        minWidth: '250px'
    },
    searchInput: {
        flex: 1,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: '14px',
        color: '#111827',
        marginLeft: '8px'
    },
    clearButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px'
    },
    filterGroup: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
    },
    filterSelect: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '30px'
    },
    select: {
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: '13px',
        color: '#4B5563',
        cursor: 'pointer'
    },
    viewToggle: {
        display: 'flex',
        gap: '4px',
        padding: '4px',
        background: '#F3F4F6',
        borderRadius: '30px'
    },
    viewButton: {
        padding: '6px 10px',
        border: 'none',
        borderRadius: '20px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s'
    },
    refreshButton: {
        padding: '8px 12px',
        background: '#F3F4F6',
        border: '1px solid #E5E7EB',
        borderRadius: '30px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: '#4B5563'
    },
    quickStats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
    },
    modernStatCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        background: 'white',
        border: '1px solid #F3F4F6',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
    },
    statLabel: {
        fontSize: '12px',
        color: '#6B7280'
    },
    statValue: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#111827'
    },
    trendBadge: {
        padding: '4px 8px',
        background: '#D1FAE5',
        borderRadius: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        fontSize: '11px',
        fontWeight: 600,
        color: '#059669'
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '24px'
    },
    gridCard: {
        padding: '20px',
        background: 'white',
        border: '1px solid #F3F4F6',
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    listItem: {
        padding: '16px 20px',
        background: 'white',
        border: '1px solid #F3F4F6',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    cardIcon: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    cardTitle: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#111827',
        margin: 0
    },
    statusBadge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 500
    },
    cardDescription: {
        fontSize: '13px',
        color: '#6B7280',
        margin: '0 0 16px 0',
        lineHeight: '1.5',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden'
    },
    cardDetails: {
        display: 'flex',
        gap: '12px',
        marginBottom: '12px'
    },
    cardDetail: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        color: '#6B7280'
    },
    interestsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '16px'
    },
    interestBadge: {
        padding: '4px 10px',
        background: '#F3E8FF',
        color: '#9333EA',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 500
    },
    moreBadge: {
        padding: '4px 10px',
        background: '#F3F4F6',
        color: '#6B7280',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 500
    },
    viewDetailsButton: {
        width: '100%',
        padding: '10px',
        background: 'linear-gradient(135deg, rgb(230 109 51), #9333EA)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    listIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    listTitle: {
        fontWeight: 600,
        color: '#111827'
    },
    listSubtitle: {
        fontSize: '12px',
        color: '#9CA3AF'
    },
    listLabel: {
        fontSize: '12px',
        color: '#9CA3AF'
    },
    listValue: {
        fontSize: '13px',
        color: '#4B5563'
    },
    iconButton: {
        padding: '8px',
        background: '#F3F4F6',
        border: 'none',
        borderRadius: '30px',
        cursor: 'pointer',
        color: '#4B5563'
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        marginTop: '24px'
    },
    pageButton: {
        padding: '8px 12px',
        border: '1px solid #E5E7EB',
        borderRadius: '30px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 500,
        transition: 'all 0.2s'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px',
        background: 'white',
        borderRadius: '16px'
    },
    spinner: {
        width: '48px',
        height: '48px',
        border: '3px solid #F3E8FF',
        borderTopColor: 'rgb(230 109 51)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px',
        background: '#FEF2F2',
        borderRadius: '16px'
    },
    retryButton: {
        marginTop: '20px',
        padding: '10px 24px',
        background: '#EF4444',
        color: 'white',
        border: 'none',
        borderRadius: '30px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center'
    },
    emptyContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px',
        background: '#F9FAFB',
        borderRadius: '16px'
    },
    clearAllButton: {
        marginTop: '20px',
        padding: '10px 24px',
        background: 'linear-gradient(135deg, rgb(230 109 51), #9333EA)',
        color: 'white',
        border: 'none',
        borderRadius: '30px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500
    },
    analyticsContainer: {
        background: 'white',
        borderRadius: '24px',
        padding: '28px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
    },
    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        background: '#F3F4F6',
        border: 'none',
        borderRadius: '30px',
        cursor: 'pointer',
        fontSize: '13px',
        color: '#4B5563',
        marginBottom: '20px'
    },
    analyticsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '24px'
    },
    analyticsTitle: {
        fontSize: '22px',
        fontWeight: 600,
        color: '#111827',
        margin: 0
    },
    campaignMeta: {
        display: 'flex',
        gap: '16px',
        marginTop: '8px'
    },
    metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '13px',
        color: '#6B7280'
    },
    periodSelector: {
        display: 'flex',
        gap: '4px',
        padding: '4px',
        background: '#F3F4F6',
        borderRadius: '30px'
    },
    periodButton: {
        padding: '6px 12px',
        border: 'none',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 500,
        transition: 'all 0.2s'
    },
    exportButton: {
        padding: '8px 16px',
        background: 'rgb(17 185 129)',
        border: '1px solid #E5E7EB',
        borderRadius: '30px',
        cursor: 'pointer',
        fontSize: '13px',
        color: 'rgb(255 255 255)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    exportMenu: {
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        minWidth: '160px',
        overflow: 'hidden'
    },
    exportItem: {
        width: '100%',
        padding: '10px 16px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '13px',
        color: '#4B5563',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        borderBottom: '1px solid #F3F4F6'
    },
    metricsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '24px'
    },
    modernMetricCard: {
        padding: '20px',
        background: 'white',
        border: '1px solid #F3F4F6',
        borderRadius: '20px',
        transition: 'all 0.2s',
        cursor: 'pointer'
    },
    modernMetricHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    modernMetricTitle: {
        fontSize: '13px',
        color: '#6B7280',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    modernMetricValue: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#111827',
        lineHeight: '1.2',
        marginBottom: '12px'
    },
    modernMetricSubtitle: {
        fontSize: '12px',
        color: '#9CA3AF',
        marginLeft: '4px',
        fontWeight: 400
    },
    modernMetricProgress: {
        marginTop: '8px'
    },
    analyticsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '20px'
    },
    analyticsCard: {
        padding: '20px',
        background: 'white',
        border: '1px solid #F3F4F6',
        borderRadius: '20px'
    },
    interactionItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
        borderBottom: '1px solid #F3F4F6'
    },
    interactionLabel: {
        fontSize: '13px',
        color: '#6B7280'
    },
    interactionValue: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#111827'
    },
    rateItem: {
        marginBottom: '16px'
    },
    rateHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '4px'
    },
    rateLabel: {
        fontSize: '13px',
        color: '#6B7280'
    },
    rateValue: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#3B82F6'
    },
    progressBarBg: {
        height: '6px',
        background: '#F3F4F6',
        borderRadius: '3px',
        overflow: 'hidden'
    },
    progressBarFill: {
        height: '100%',
        borderRadius: '3px',
        transition: 'width 0.5s ease'
    },
    indicatorGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginTop: '16px'
    },
    indicator: {
        padding: '12px',
        background: '#F9FAFB',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    indicatorLabel: {
        fontSize: '11px',
        color: '#9CA3AF'
    },
    indicatorValue: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#111827'
    },
    ratiosGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '8px',
        marginTop: '12px',
        padding: '12px',
        background: '#F9FAFB',
        borderRadius: '12px',
        textAlign: 'center'
    },
    ratioLabel: {
        fontSize: '11px',
        color: '#9CA3AF'
    },
    ratioValue: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#EC4899'
    },
    ringCenter: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center'
    },
    ringPercentage: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#8B5CF6'
    },
    ringLabel: {
        fontSize: '10px',
        color: '#9CA3AF'
    },
    detailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px',
        marginBottom: '20px'
    },
    detailsCard: {
        padding: '20px',
        background: '#F9FAFB',
        borderRadius: '20px'
    },
    detailsTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#111827',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    timeline: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    timelineItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    timelineIcon: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    timelineTitle: {
        fontWeight: 500,
        color: '#111827'
    },
    timelineTime: {
        fontSize: '11px',
        color: '#6B7280'
    },
    statusBadgeSmall: {
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '10px',
        fontWeight: 500
    },
    targetingGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px'
    },
    targetingLabel: {
        fontSize: '11px',
        color: '#9CA3AF',
        marginBottom: '4px'
    },
    targetingValue: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827'
    },
    interestsGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '8px'
    },
    successMetrics: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginTop: '20px',
        padding: '20px',
        background: 'linear-gradient(135deg, #F9FAFB, #F3F4F6)',
        borderRadius: '20px'
    },
    successItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    successLabel: {
        fontSize: '11px',
        color: '#9CA3AF'
    },
    successValue: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#111827'
    },
    refreshAnalyticsButton: {
        padding: '10px 20px',
        background: '#F3F4F6',
        border: '1px solid #E5E7EB',
        borderRadius: '30px',
        cursor: 'pointer',
        fontSize: '13px',
        color: '#4B5563',
        display: 'flex',
        alignItems: 'center'
    }
};

export default CampaignAnalytics;